import VenueCard from "@/components/template/VenueCard";
import VenueCardSkeleton from "@/components/template/VenueCardSkeleton";
import {
  useAuth,
  useFilters,
  useHomeVenues,
  useI18N,
  useSavedPlaces,
} from "common/hooks";
import { Venue } from "common/types";
import NoSearchResults from "@/assets/images/noresults.svg";
import NoSavcedPlacesResults from "@/assets/images/EmptyList.svg";
import NoSearchResultsFilter from "@/assets/images/noresultsfilter.svg";
import { useAtom } from "jotai";
import {
  filtersOpenAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  venueDetailsOpenedAtom,
} from "@/state/atoms";
import { useEffect, useState, useRef } from "react";
import Modal from "react-responsive-modal";
import CreateList from "@/components/common/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/common/SavedPlaces/AddRemoveVenueToList";
import AnalyticsService from "@/services/analytics";
import { useNavigate } from "@tanstack/react-router";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";

type VenueListProps = {
  onVenueClick: (location: Venue) => void;
};

function VenueList(props: VenueListProps) {
  const { locations, isLoading } = useHomeVenues();
  const { filtersState } = useFilters();
  const { user } = useAuth();
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);
  const { refetch, data } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];
  const [listNameCreated, setListNameCreated] = useState("");
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [refetch, user]);

  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode
    );

    AnalyticsService.trackGAEventHandler("mySavedPlacesExitSave");
    AnalyticsService.trackMx("mySavedPlacesExitSave", {});

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button  and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  return isLoading ? (
    <div className="grid md:grid-cols-2 gap-6 md:gap-6">
      {Array.from({ length: 4 }).map((_, index) => (
        <VenueCardSkeleton key={index} />
      ))}
    </div>
  ) : !isLoading && locations.length == 0 ? (
    filtersState.isFiltersApplied && !savedPlaceIdToView ? (
      <VenueListNoFilterResults />
    ) : savedPlaceIdToView ? (
      <VenueListNoSavedPlacesResults />
    ) : (
      <VenueListNoSearchResults />
    )
  ) : (
    <div
      className="grid pb-10 md:grid-cols-2 gap-3 md:gap-3"
      role="listitem"
      aria-label="venue"
    >
      {locations.map((location) => (
        <VenueCard
          key={location.id}
          location={location}
          onOpen={(location) => props.onVenueClick(location)}
          onSaveVenue={handleSaveVenue}
          savedPlaces={savedPlaces}
        />
      ))}

      {savedPlaceIdToView && locations.length <= 4 && (
        <DiscoverMoreVenuesCard />
      )}

      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={location}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={location}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

export default VenueList;

function DiscoverMoreVenuesCard() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const navigate = useNavigate({ from: "/" });

  const { setSelectedVenue } = useSelectedVenue();
  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);

  const clearsetSelectedVenue = () => {
    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    navigate({ to: "/" });
  };

  return (
    <div className="w-full h-[396px] px-2 py-6 bg-primary-50 dark:bg-secondary-800 rounded-xl flex-col justify-center items-center gap-4 inline-flex">
      {/* Icon Container */}
      <div className="flex justify-center items-center mb-2">
        <img
          src={
            isDark
              ? "/images/MagnifyingGlass_discover_dark.svg"
              : "/images/MagnifyingGlass_discover.svg"
          }
          alt=""
          aria-hidden="true"
        />
      </div>

      <div className="flex-col justify-start items-start gap-1 flex">
        <div
          className="self-stretch h-5 text-center text-primary-800 dark:text-primary-150 mobile-h2-large-semi-bold"
          tabIndex={0}
        >
          {i18n.get("discoverMoreVenues")}
          <br />
        </div>
      </div>

      <div
        className="w-full text-center text-primary-800 dark:text-primary-150 paragraph-small-medium max-w-[80%]"
        tabIndex={0}
      >
        {i18n.get("discoverMoreVenuesMessage")}
      </div>

      <div className="flex flex-col justify-center items-center w-full py-3 max-w-[80%]">
        <WideButton className="grow" onClick={() => clearsetSelectedVenue()}>
          {i18n.get("explore")}
        </WideButton>
      </div>
    </div>
  );
}

function VenueListNoSavedPlacesResults() {
  const i18n = useI18N();
  const navigate = useNavigate({ from: "/" });

  const { setSelectedVenue } = useSelectedVenue();
  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);

  const noSavedPlacesRef = useRef(null); // Reference for the paragraph

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noSavedPlacesRef.current) {
      noSavedPlacesRef.current.focus();
    }
  }, []);

  const clearsetSelectedVenue = () => {
    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");
    navigate({ to: "/" });
  };

  return (
    <div
      className="flex flex-col items-center justify-center flex-1"
      aria-label="No venue found"
    >
      <img
        src={NoSavcedPlacesResults}
        alt={i18n.get("savedPlacesListEmpty")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noSavedPlacesRef} // Attach the ref here
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center desktop-h4-medium-bold text-primary-800 dark:text-primary-150"
        tabIndex={0}
      >
        {i18n.get("savedPlacesListEmpty")}
      </p>
      <p
        className="paragraph-small-regular py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center text-natural-700 dark:text-primary-10"
        tabIndex={0}
      >
        {i18n.get("savedPlacesListEmptyMessage")}
      </p>

      <div
        className="flex flex-col justify-center items-center w-full py-6 md:py-3 md:max-w-[40%]"
        tabIndex={0}
      >
        <WideButton className="grow" onClick={() => clearsetSelectedVenue()}>
          {i18n.get("exploreNearby")}
        </WideButton>
      </div>
    </div>
  );
}

function VenueListNoSearchResults() {
  const i18n = useI18N();
  const noResultsRef = useRef(null); // Reference for the paragraph

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 mt-5"
      aria-label="No venue found"
      tabIndex={-1}
    >
      <img
        src={NoSearchResults}
        alt={i18n.get("noResultsFound")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noResultsRef} // Attach the ref here
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center desktop-h4-medium-bold text-primary-800 dark:text-primary-150"
        tabIndex={0}
      >
        {i18n.get("noResultsFound")}
      </p>
      <p
        className="paragraph-small-regular py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center  text-natural-700 dark:text-primary-10"
        tabIndex={0}
      >
        {i18n.get("bitLostMessageSearch")}
      </p>
    </div>
  );
}

function VenueListNoFilterResults() {
  const i18n = useI18N();
  const [, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);

  const noFilterResultsRef = useRef(null); // Reference for the paragraph

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (noFilterResultsRef.current) {
      noFilterResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 mt-5"
      aria-label="No venue found"
    >
      <img
        src={NoSearchResultsFilter}
        alt={i18n.get("noMatches")}
        className="md:max-w-[50%] py-2"
        aria-hidden="true"
      />
      <p
        ref={noFilterResultsRef} // Attach the ref here
        className="mt-3 py-2 md:py-0 mx-auto text-left md:text-center desktop-h4-medium-bold text-primary-800 dark:text-primary-150"
        tabIndex={0}
      >
        {i18n.get("noMatches")}
      </p>
      <p
        className="paragraph-small-regular py-2 md:py-0 md:w-[444px] mt-2 mx-auto text-left md:text-center text-natural-700 dark:text-primary-10"
        tabIndex={0}
      >
        {i18n.get("tryUpdateFilters")}
      </p>

      <div
        className="flex flex-col justify-center items-center w-full py-6 md:py-3 md:max-w-[40%]"
        tabIndex={0}
      >
        <WideButton
          className="grow"
          onClick={() => {
            setIsFilterSheetOpen(true);
          }}
        >
          {i18n.get("updateFilters")}
        </WideButton>
      </div>
    </div>
  );
}
